import { useEffect } from 'react';
import { UploadFile } from 'antd/lib/upload/interface';
import {
  IIssueTypeImplementationPayload,
  IIssueTypeInProgress,
  ITaskInProgress,
  IUploadMidTidResponse,
  ITaskInProgressParams,
  IAssignTicketsPayload,
  IGetSposContractParams,
  ISposContract,
  AnyObject,
  ISposContractDetail,
  IUpdateSposContractStatus,
} from 'interfaces';
import { useState } from 'react';
import {
  fileService,
  implementServices,
  issueTypeService,
  sposServices,
} from 'services';
import {
  responseConstants,
  dateTimeConstants,
  sposConstants,
} from 'constants/index';
import { PaginationProps } from 'antd/es/pagination';
import { paginationHelpers, locationHelpers } from 'helpers';
import { commonConstants } from 'constants/index';
import { pickBy } from 'lodash';
import { defaultPage } from 'constants/pagination';
import dayjs from 'dayjs';

const { DEFAULT_SIZE_CHANGER, DEFAULT_PARAMS } = commonConstants;
const { mapPagerApiToAntdPager } = paginationHelpers;
const { CODE } = responseConstants;
const { generateLocationId } = locationHelpers;
const { YEAR_DASH_FORMAT } = dateTimeConstants;
const { EAppraisalStatus } = sposConstants;

const useUploadMidTid = () => {
  const [uploading, setUploading] = useState<boolean>(false);
  const [resultUpload, setResultUpload] = useState<
    IUploadMidTidResponse['data'] & { fileName: string }
  >();
  const [fileError, setFileError] = useState<string>();
  const uploadFileMidTid = async (file: UploadFile) => {
    try {
      setUploading(true);
      setFileError(undefined);
      setResultUpload(undefined);
      if (file.originFileObj) {
        const res = await fileService.uploadFile(file.originFileObj);
        if (res && res.data.fileName) {
          const result = await implementServices.uploadFileMidTid({
            filePath: res.data.fileName,
          });
          if (result.code === CODE.DONE) {
            setResultUpload({ ...result.data, fileName: res.data.fileName });
          }
        }
      }
    } finally {
      setUploading(false);
    }
  };

  return {
    uploadFileMidTid,
    uploading,
    fileError,
    resultUpload,
    setResultUpload,
    setFileError,
  };
};

const useIssueTypeInProgress = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [issueTypeInProgress, setIssueTypeInProgress] = useState<
    IIssueTypeInProgress[]
  >([]);
  const getIssueTypeInProgress = async () => {
    try {
      setLoading(true);
      const res = await issueTypeService.getIssueTypeInProgressByUser();
      if (res && res.data.issueTypes) {
        setIssueTypeInProgress(res.data.issueTypes);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getIssueTypeInProgress();
  }, []);

  return {
    issueTypeInProgress,
    getIssueTypeInProgress,
    loading,
  };
};

const useIssueTypeImplementation = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const postIssueTypeImplementation = async (
    payload: IIssueTypeImplementationPayload
  ) => {
    try {
      setLoading(true);
      await implementServices.postIssueTypeImplementation(payload);
    } finally {
      setLoading(false);
    }
  };

  return {
    postIssueTypeImplementation,
    loading,
  };
};

const useTaskInProgress = (defaultParams: ITaskInProgressParams) => {
  const [tasksInProgress, setTasksInProgress] = useState<ITaskInProgress[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingAssign, setLoadingAssign] = useState<boolean>(false);
  const [params, setParams] = useState<ITaskInProgressParams>(defaultParams);
  const [paginationAnt, setPaginationAnt] = useState<PaginationProps>({
    ...DEFAULT_SIZE_CHANGER,
  });

  const getTaskInProgress = async () => {
    try {
      setLoading(true);
      const res = await implementServices.getTaskInProgress(params);
      if (res && res.data.implementationTickets) {
        setTasksInProgress(res.data.implementationTickets);
        const { pagination } = res.data;
        const pager = mapPagerApiToAntdPager(pagination);
        setPaginationAnt(pager);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: PaginationProps) => {
    const newQueryParams = {
      ...params,
      page: pagination.current || params.page,
      pageSize: pagination.pageSize || params.pageSize,
    };
    setParams(newQueryParams);
  };

  const onSearch = (values: any) => {
    values.locationCode = values.provinceCode
      ? generateLocationId(
          values.provinceCode,
          values.districtCode,
          values.wardCode
        )
      : undefined;

    let query = {
      ...pickBy(values, (e, key) => {
        return (
          e !== '' &&
          e !== undefined &&
          e !== null &&
          e !== commonConstants.ALL &&
          !['provinceCode', 'districtCode', 'wardCode'].includes(key)
        );
      }),
    };
    setParams({ ...query, page: defaultPage, pageSize: params.pageSize });
  };

  const onClearFilter = () => {
    setParams(defaultParams);
  };

  const assignTicket = async (payload: IAssignTicketsPayload) => {
    try {
      setLoadingAssign(true);
      await implementServices.assignTickets(payload);
    } finally {
      setLoadingAssign(false);
    }
  };

  useEffect(() => {
    getTaskInProgress();
  }, [params]);

  return {
    getTaskInProgress,
    tasksInProgress,
    loading,
    handleTableChange,
    paginationAnt,
    onSearch,
    onClearFilter,
    assignTicket,
    loadingAssign,
    setLoading,
  };
};

const useListSPOS = () => {
  const [params, setParams] = useState<IGetSposContractParams>({
    ...DEFAULT_PARAMS,
  });
  const [listContractSpos, setListContractSpos] = useState<ISposContract[]>([]);
  const [paginationAnt, setPaginationAnt] = useState<PaginationProps>({
    ...DEFAULT_SIZE_CHANGER,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingExportMidTid, setLoadingExportMidTid] = useState<boolean>(
    false
  );
  const [loadingAssignAppraiser, setLoadingAssignAppraiser] = useState<boolean>(
    false
  );
  const [loadingImmediately, setLoadingImmediately] = useState<boolean>(false);

  const getListSpos = async () => {
    try {
      setLoading(true);
      const res = await sposServices.getListContractSpos(params);
      if (res && res.sposContracts) {
        setListContractSpos(res.sposContracts);
        const { pagination } = res;
        const pager = mapPagerApiToAntdPager(pagination);
        setPaginationAnt(pager);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: PaginationProps) => {
    const newQueryParams = {
      ...params,
      page: pagination.current || params.page,
      pageSize: pagination.pageSize || params.pageSize,
    };
    setParams(newQueryParams);
  };

  const onSearch = (values: AnyObject) => {
    if (values.submissionDate && values.submissionDate.length) {
      values.createdFrom = dayjs(values.submissionDate[0]).format(
        YEAR_DASH_FORMAT
      );
      values.createdTo = dayjs(values.submissionDate[1]).format(
        YEAR_DASH_FORMAT
      );
    }

    if (values.approvalDate && values.approvalDate.length) {
      values.approved2From = dayjs(values.approvalDate[0]).format(
        YEAR_DASH_FORMAT
      );
      values.approved2To = dayjs(values.approvalDate[1]).format(
        YEAR_DASH_FORMAT
      );
    }

    let query = {
      ...pickBy(values, (e, key) => {
        return (
          e !== '' &&
          e !== undefined &&
          e !== null &&
          !['submissionDate', 'approvalDate'].includes(key)
        );
      }),
    };
    setParams({ ...query, page: defaultPage, pageSize: params.pageSize });
  };

  const exportListMidTidBank = async (values: {
    date: string[];
    exportType: number;
  }) => {
    try {
      setLoadingExportMidTid(true);
      const payload = {
        createdFrom: dayjs(values['date'][0]).format(YEAR_DASH_FORMAT),
        toDate: dayjs(values['date'][1]).format(YEAR_DASH_FORMAT),
        exportType: values['exportType'],
      };

      const fileName = `Danh sách xin cấp mid/tid bank từ ${dayjs(
        values['date'][0]
      ).format(YEAR_DASH_FORMAT)}_${dayjs(values['date'][1]).format(
        YEAR_DASH_FORMAT
      )}.xlsx`;
      const res = await sposServices.exportListMidTidBank(payload);
      if (res.filePath) {
        await fileService.downloadFileToDevice(res.filePath, fileName);
      }
    } finally {
      setLoadingExportMidTid(false);
    }
  };

  const assignSposAppraiser = async (
    sposContractIds: number[],
    sposAppraiserId: number
  ) => {
    try {
      setLoadingAssignAppraiser(true);
      const assignPromise = sposContractIds.map(sposContractId => {
        return sposServices.updateContractStatus(sposContractId, {
          sposAppraiserId: sposAppraiserId,
          sposContractStatus: EAppraisalStatus.Appraising,
        });
      });
      await Promise.all(assignPromise);
    } finally {
      setLoadingAssignAppraiser(false);
    }
  };

  const updateContractStatus = async (
    sposContractId: number,
    payload: IUpdateSposContractStatus
  ) => {
    return await sposServices.updateContractStatus(sposContractId, payload);
  };

  return {
    onSearch,
    loading,
    listContractSpos,
    handleTableChange,
    paginationAnt,
    exportListMidTidBank,
    loadingExportMidTid,
    assignSposAppraiser,
    loadingAssignAppraiser,
    getListSpos,
    params,
    updateContractStatus,
    loadingImmediately,
    setLoadingImmediately,
  };
};

const useSposDetail = () => {
  const [sposDetail, setSposDetail] = useState<ISposContractDetail>();
  const [loading, setLoading] = useState<boolean>(false);

  const getSposDetail = async (sposContractId: number) => {
    try {
      setLoading(true);
      const res = await sposServices.getSposContract(sposContractId);
      setSposDetail(res);
    } finally {
      setLoading(false);
    }
  };
  return {
    getSposDetail,
    loading,
    sposDetail,
  };
};

export default {
  useUploadMidTid,
  useIssueTypeInProgress,
  useIssueTypeImplementation,
  useTaskInProgress,
  useListSPOS,
  useSposDetail,
};
